import React, { useRef, useLayoutEffect, useCallback } from "react";
import * as M from "./style";
import { formatDate, formatTime, getTodayDateString, getYesterdayDateString, getTimestampFromDateString, parseCustomDateFormat } from "../../Utils/dateFormatter";
import userImg from "../../Assets/Images/user-profile-img.webp";
import { useSelector } from "react-redux";
import { RootState } from "../../Redux/store";
import { Message } from "../../Config/interface.config";
import { getImageUrl } from "../../Utils/helpers";

const MessageSkeleton = () => (
  <div style={{ display: "flex", padding: "8px 0", alignItems: "center" }}>
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: "100%",
        background: "#444",
        marginRight: 12,
      }}
    />
    <div style={{ flex: 1 }}>
      <div
        style={{
          background: "#333",
          height: 14,
          width: "70%",
          marginBottom: 6,
          borderRadius: 8,
        }}
      />
      <div
        style={{
          background: "#333",
          height: 10,
          width: "30%",
          borderRadius: 8,
        }}
      />
    </div>
  </div>
);

const getAvatarUrl = (fileURL?: string | null) => {
  return getImageUrl(fileURL || "") || userImg;
};

interface MessagesProps {
  channelSlug: string;
  messages: Message[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  messagesEndRef: React.RefObject<HTMLDivElement>;
}

const Messages: React.FC<MessagesProps> = ({
  channelSlug,
  messages,
  loading,
  hasMore,
  loadMore,
  messagesEndRef,
}) => {
  // CORRECTED: Fetch current user's ID and image details
  const { id: userId, image: userImage } = useSelector(
    (state: RootState) => state.user
  );

  const msgWrapperRef = useRef<HTMLDivElement | null>(null);

  // Scroll logic for fixing infinite scroll position (recommended)
  const prevScrollHeightRef = useRef<number>(0);
  const isInitialLoadRef = useRef<boolean>(true);

  // Infinite up-scroll: load more older messages
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const handleScroll = useCallback(() => {
    const wrapper = msgWrapperRef.current;
    if (!wrapper || !hasMore || loading) return;

    if (wrapper.scrollTop < 80) {
      if (debounceRef.current) return;

      debounceRef.current = setTimeout(() => {
        loadMore(); // your redux dispatch or function to fetch next page
        debounceRef.current = null;
      }, 300); // 300ms debounce
    }
  }, [hasMore, loading, loadMore]);

  // Scroll to bottom on initial mount (Recommended Scroll Fix 1/2)
  useLayoutEffect(() => {
    // Only scroll to bottom on the FIRST render after initial messages are loaded
    if (
      msgWrapperRef.current &&
      isInitialLoadRef.current &&
      !loading &&
      messages.length > 0
    ) {
      msgWrapperRef.current.scrollTop = msgWrapperRef.current.scrollHeight;
      isInitialLoadRef.current = false; // Initial load is complete
    }
    // Also scroll to bottom if the last message was sent by the current user (optional, for better UX)
    const lastMessage = messages[messages.length - 1];
    const isLocalSender =
      lastMessage &&
      (typeof lastMessage.from === "string"
        ? lastMessage.from === userId
        : lastMessage.from?.id === userId);

    if (msgWrapperRef.current && isLocalSender) {
      msgWrapperRef.current.scrollTop = msgWrapperRef.current.scrollHeight;
    }
  }, [loading, messages.length, userId, messages]);

  // Adjust scroll position when older messages are loaded (Recommended Scroll Fix 2/2)
  useLayoutEffect(() => {
    const wrapper = msgWrapperRef.current;
    if (wrapper && prevScrollHeightRef.current > 0) {
      // Calculate the height added by the newly loaded (older) messages
      const newScrollHeight = wrapper.scrollHeight;
      const addedHeight = newScrollHeight - prevScrollHeightRef.current;

      // Maintain the user's view position by shifting the scroll up
      wrapper.scrollTop = wrapper.scrollTop + addedHeight;
    }

    // Always update the scroll height for the next render cycle
    prevScrollHeightRef.current = wrapper ? wrapper.scrollHeight : 0;
  }, [messages.length]);

  // Group messages by date
  const groupedMessages = React.useMemo(() => {
    const sortedMessages = [...messages].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return aTime - bTime;
    });
    return sortedMessages.reduce((acc: Record<string, Message[]>, msg) => {
      const day = msg.createdAt?.split("T")[0]?.split("-");
      const date = day ? `${parseInt(day[2])}-${day[1]}-${day[0]}` : formatDate(msg.createdAt);
      if (!acc[date]) acc[date] = [];
      acc[date].push(msg);
      return acc;
    }, {});
  }, [messages]);

  const sortedDateKeys = Object.keys(groupedMessages).sort((a, b) => {
    return (
      getTimestampFromDateString(a) - getTimestampFromDateString(b)
    );
  });

  const getMessageDate = (key: string) => {
    const today = getTodayDateString();
    const yesterday = getYesterdayDateString();
    if (key === today) return "Today";
    if (key === yesterday) return "Yesterday";
    return parseCustomDateFormat(key);
  };

  const getMessageText = (message: Message) => {
    if (message.messageType !== "gift") return message.text;

    const fromId =
      typeof message.from === "string"
        ? message.from
        : message.from?._id || message.from?.id;
    const amountInPaise =
      fromId === userId ? message.giftAmount : message.giftCreatorAmount;

    if (!Number.isFinite(Number(amountInPaise)) || Number(amountInPaise) <= 0) {
      return message.text;
    }

    const amount = Number(amountInPaise) / 100;
    return fromId === userId
      ? `🎁 You sent a gift of ₹${amount}`
      : `🎁 You received a gift of ₹${amount}`;
  };

  return (
    <M.MessagesWrapper
      ref={msgWrapperRef}
      onScroll={handleScroll}
      style={{ overflowY: "auto" }}
    >
      {loading && hasMore && (
        <div style={{ padding: "10px" }}>
          {[1, 2, 3].map((n) => (
            <MessageSkeleton key={n} />
          ))}
        </div>
      )}
      {sortedDateKeys.map((date) => (
        <M.MessageBox key={`message-date-${date}`} className="text-white">
          {groupedMessages[date].length > 0 && (
            <M.MessageDate>{getMessageDate(date)}</M.MessageDate>
          )}
          <M.Messages>
            {groupedMessages[date].map((mess, index) => {
              const fromId =
                typeof mess.from === "string"
                  ? mess.from
                  : mess.from?._id || mess.from?.id;
              const isRemote = fromId !== userId; // isRemote is TRUE for the other person, FALSE for the current user
              // FIX: Determine the correct avatar URL based on sender
              const avatarUrl = isRemote
                ? // Remote message: Use the image from the message sender object (mess.from)
                  typeof mess.from === "object" && mess.from?.image?.fileURL
                  ? getAvatarUrl(mess.from.image.fileURL)
                  : userImg
                : // Local message: Use the current user's image from Redux state (userImage)
                  getAvatarUrl(userImage?.fileURL);

              return (
                <M.TextMessageWrapper
                  key={`${date}-message-${mess._id || index}`} // Use mess._id for a stable key
                  isRemote={isRemote} // Pass isRemote to the styled component
                >
                  <M.ProfileImg
                    alt="user"
                    src={avatarUrl}
                    onError={(event) => {
                      if (event.currentTarget.src !== userImg) {
                        event.currentTarget.src = userImg;
                      }
                    }}
                  />
                  <M.Message isRemote={isRemote}>
                    <M.Text>{getMessageText(mess)}</M.Text>
                    <M.Time isRemote={isRemote}>
                      {formatTime(mess.createdAt)}
                    </M.Time>
                  </M.Message>
                </M.TextMessageWrapper>
              );
            })}
          </M.Messages>
        </M.MessageBox>
      ))}
      <div ref={messagesEndRef} />
      {!loading && (!messages || messages.length === 0) && (
        <div style={{ color: "#aaa", textAlign: "center", marginTop: 40 }}>
          No messages yet.
        </div>
      )}
    </M.MessagesWrapper>
  );
};

export default Messages;
